import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { genUuid } from '../utils/util';
import { useUbcSend } from './useUbcSend';

/** 心跳间隔（秒） */
const HEARTBEAT_SEC = 3;
/** 结束计时段时，尾段小于该阈值则丢弃（秒） */
const TAIL_MIN_SEC = 0.5;

/**
 * 页面时长埋点 hook（心跳 + 页面级 uuid 方案）
 *
 * - 页面刷新后生成一个新 uuid；同一次进入系统内（路由切换、hidden→visible）uuid 保持不变。
 * - 每 3s 上报一次心跳埋点，携带当前系统会话 uuid。
 * - 路由切换、hidden、pagehide、卸载时先 flush 一次尾段。
 *
 * 后端统计：
 *  - 单次页面停留时长：SUM(duration) GROUP BY uuid
 *  - 页面平均停留 / PV：按 page 聚合后二次统计
 *
 * 覆盖场景：SPA 路由切换、切 Tab、锁屏、关闭/刷新浏览器。
 */
export function usePageLeave({ enabled = true }: { enabled?: boolean } = {}) {
    const location = useLocation();
    const { ubcSend } = useUbcSend();
    const uuidRef = useRef('');
    const lastFlushTimeRef = useRef(0);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const activeRef = useRef(false);
    const initializedRef = useRef(false);
    const enabledRef = useRef(enabled);

    useEffect(() => {
        enabledRef.current = enabled;
        if (!enabled) stopTiming.current();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabled]);

    const sendHeartbeat = useRef((duration: number, isFinal: boolean) => {
        if (!enabledRef.current) return;
        if (duration <= 0) return;
        if (isFinal && duration < TAIL_MIN_SEC) return;
        ubcSend({
            type: 'timing',
            value: 'page',
            duration: Number(duration.toFixed(3)),
            ext: {
                product_info: {
                    uuid: uuidRef.current,
                    final: isFinal ? 1 : 0,
                }
            },
        });
    });

    const heartbeat = useRef(() => {
        if (!activeRef.current) return;
        const now = Date.now();
        const duration = (now - lastFlushTimeRef.current) / 1000;
        lastFlushTimeRef.current = now;
        sendHeartbeat.current(duration, false);
    });

    const stopTiming = useRef(() => {
        if (!activeRef.current) return;
        activeRef.current = false;
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
        const now = Date.now();
        const duration = (now - lastFlushTimeRef.current) / 1000;
        sendHeartbeat.current(duration, true);
    });

    const startTiming = useRef(() => {
        if (!enabledRef.current) return;
        if (activeRef.current) return;
        // uuid 仅在页面刷新后重置；同一次进入系统内保持不变
        if (!uuidRef.current) {
            uuidRef.current = genUuid();
        }
        lastFlushTimeRef.current = Date.now();
        activeRef.current = true;
        timerRef.current = setInterval(() => heartbeat.current(), HEARTBEAT_SEC * 1000);
    });

    // 挂载时初始化，注册 visibilitychange / pagehide 监听
    useEffect(() => {
        // 固定本次 effect 使用的函数引用，避免 cleanup 阶段 ref.current 漂移告警
        const start = startTiming.current;
        const stop = stopTiming.current;

        // 页面加载后可能已处于 hidden（如后台打开的 Tab），此时等 visible 再开启
        if (!document.hidden) {
            start();
        }

        const onVisibilityChange = () => {
            if (document.hidden) {
                stop();
            } else {
                start();
            }
        };
        const onPageHide = () => stop();

        document.addEventListener('visibilitychange', onVisibilityChange);
        window.addEventListener('pagehide', onPageHide);

        return () => {
            document.removeEventListener('visibilitychange', onVisibilityChange);
            window.removeEventListener('pagehide', onPageHide);
            stop();
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // 路由切换：flush 旧页面尾段，切换 page 后继续计时（同一次进入系统内 uuid 不变）
    useEffect(() => {
        if (!initializedRef.current) {
            initializedRef.current = true; // 首次挂载已在上面 effect 处理
            return;
        }
        stopTiming.current();
        if (!document.hidden) {
            startTiming.current();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);
}
